import type {
  AccountListModel,
  AccountMeetingLocationModel,
  AccountModel,
  StaffMemberCategory,
} from '../app/types/account.type.ts';
import type { AccountCategoryModel } from '../app/types/account-category.type.ts';
import type { AccountSpecialtyModel } from '../app/types/specialty.type.ts';
import baseApi, { CacheTag } from './meeting-md-api-slice';

const accountApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    fetchAccount: builder.query<AccountModel | null, string>({
      query: key => ({ url: `/account/${key}` }),
      providesTags: [CacheTag.Account],
    }),
    fetchCurrentAccount: builder.query<AccountModel, void>({
      query: () => ({ url: '/account/current' }),
      providesTags: [CacheTag.Account, CacheTag.CurrentUser],
    }),
    postAccount: builder.mutation<AccountModel, AccountModel>({
      query: post => ({
        url: '/account',
        method: 'POST',
        body: post,
      }),
      invalidatesTags: [CacheTag.Account, CacheTag.User, CacheTag.CurrentUser],
    }),
    putAccount: builder.mutation<AccountModel, Partial<AccountModel>>({
      query: put => ({
        url: '/account',
        method: 'PUT',
        body: put,
      }),
      invalidatesTags: [CacheTag.Account],
    }),
    deactivateAccount: builder.mutation<AccountModel, string>({
      query: key => ({
        url: `/account/deactivate/${key}`,
        method: 'PUT',
      }),
      invalidatesTags: [CacheTag.Account],
    }),
    reactivateAccount: builder.mutation<AccountModel, string>({
      query: key => ({
        url: `/account/reactivate/${key}`,
        method: 'PUT',
      }),
      invalidatesTags: [CacheTag.Account],
    }),
    setAccountFlag: builder.mutation<AccountModel, { key: string; accountFlagId: number }>({
      query: ({ key, accountFlagId }) => ({
        url: `/account/flag/${key}`,
        method: 'PUT',
        body: { accountFlagId },
      }),
      invalidatesTags: [CacheTag.Account],
    }),
    postAccountMeetingLocation: builder.mutation<AccountModel, AccountMeetingLocationModel>({
      query: post => ({
        url: '/account/location',
        method: 'POST',
        body: post,
      }),
      invalidatesTags: [CacheTag.Account],
    }),
    putAccountMeetingLocation: builder.mutation<AccountModel, AccountMeetingLocationModel>({
      query: put => ({
        url: '/account/location',
        method: 'PUT',
        body: put,
      }),
      invalidatesTags: [CacheTag.Account],
    }),
    deleteAccountMeetingLocation: builder.mutation<
      AccountModel,
      { accountKey: string; id: number }
    >({
      query: obj => ({
        url: `/account/location/${obj.accountKey}/${obj.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [CacheTag.Account],
    }),
    postAccountSpecialty: builder.mutation<AccountModel, AccountSpecialtyModel>({
      query: post => ({
        url: '/account/specialty',
        method: 'POST',
        body: post,
      }),
      invalidatesTags: [CacheTag.Account],
    }),
    deleteAccountSpecialty: builder.mutation<AccountModel, { accountKey: string; id: number }>({
      query: obj => ({
        url: `/account/specialty/${obj.accountKey}/${obj.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [CacheTag.Account],
    }),
    postAccountCategory: builder.mutation<AccountModel, AccountCategoryModel>({
      query: post => ({
        url: '/account/category',
        method: 'POST',
        body: post,
      }),
      invalidatesTags: [CacheTag.Account],
    }),
    putAccountCategory: builder.mutation<AccountModel, AccountCategoryModel>({
      query: put => ({
        url: '/account/category',
        method: 'PUT',
        body: put,
      }),
      invalidatesTags: [CacheTag.Account],
    }),
    deleteAccountCategory: builder.mutation<AccountModel, { accountKey: string; id: number }>({
      query: obj => ({
        url: `/account/category/${obj.accountKey}/ ${obj.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [CacheTag.Account],
    }),
    postStaffMemberCategory: builder.mutation<AccountModel, StaffMemberCategory>({
      query: post => ({
        url: '/account/staffMemberCategory',
        method: 'POST',
        body: post,
      }),
      invalidatesTags: [CacheTag.Account],
    }),
    putStaffMemberCategory: builder.mutation<AccountModel, StaffMemberCategory>({
      query: put => ({
        url: '/account/staffMemberCategory',
        method: 'PUT',
        body: put,
      }),
      invalidatesTags: [CacheTag.Account],
    }),
    deleteStaffMemberCategory: builder.mutation<AccountModel, { accountKey: string; id: number }>({
      query: obj => ({
        url: `/account/staffMemberCategory/${obj.accountKey}/${obj.id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [CacheTag.Account],
    }),
    fetchVisibleAccounts: builder.query<AccountListModel[], { organizationKey?: string }>({
      query: obj => {
        if (obj?.organizationKey) {
          const params = new URLSearchParams();

          if (obj.organizationKey) {
            params.set('organizationKey', obj.organizationKey);
          }

          return `/Account/Visible?${params.toString()}`;
        }
        return '/Account/Visible';
      },
      providesTags: [CacheTag.Account, CacheTag.CurrentUser],
    }),
    fetchAccountCategories: builder.query<AccountCategoryModel[], void>({
      query: () => ({ url: '/AccountCategory' }),
      providesTags: [CacheTag.Account],
    }),
    fetchAccountName: builder.query<{ accountName: string }, string>({
      query: key => ({ url: `/Account/Name/${key}` }),
    }),
  }),
});

export const {
  useFetchAccountQuery,
  usePostAccountMutation,
  usePutAccountMutation,
  useDeactivateAccountMutation,
  useReactivateAccountMutation,
  useSetAccountFlagMutation,
  usePostAccountMeetingLocationMutation,
  usePutAccountMeetingLocationMutation,
  useDeleteAccountMeetingLocationMutation,
  usePostAccountSpecialtyMutation,
  useDeleteAccountSpecialtyMutation,
  usePostAccountCategoryMutation,
  usePutAccountCategoryMutation,
  useDeleteAccountCategoryMutation,
  useFetchCurrentAccountQuery,
  usePostStaffMemberCategoryMutation,
  usePutStaffMemberCategoryMutation,
  useDeleteStaffMemberCategoryMutation,
  useFetchVisibleAccountsQuery,
  useFetchAccountCategoriesQuery,
  useFetchAccountNameQuery,
} = accountApi;
